import { useCallback } from 'react';
import { Table, TableBody, TableCell, TableContainer, TableRow, Typography, Paper } from '@mui/material';
import { Table as TanStackTable, flexRender } from '@tanstack/react-table';
import TableHeader from './TableHeader';
import type { ColumnFilterWithDisplayLabel, FilterConfig } from './TableHeader';

interface TableBaseProps<TData extends object> {
    table: TanStackTable<TData>;
    noDataMessage?: string;
    filters?: FilterConfig[];
    columnFilters?: ColumnFilterWithDisplayLabel[];
    onFiltersChange?: (filters: ColumnFilterWithDisplayLabel[]) => void;
    onRowClick?: (row: TData) => void;
}

/**
 * Renders the base table with header and body.
 *
 * @template TData
 * @param {TableBaseProps<TData>} props
 * @returns {JSX.Element}
 */
export const TableBase = <TData extends object>({
    table,
    noDataMessage = 'No data found',
    filters = [],
    columnFilters = [],
    onFiltersChange,
    onRowClick,
}: TableBaseProps<TData>) => {
    const getRowStyles = useCallback(
        (index: number) => ({
            bgcolor: index % 2 === 0 ? 'background.paper' : 'gray.10',
            '&:hover': {
                bgcolor: 'action.hover',
                transition: 'background-color 0.2s ease',
            },
            '&:last-child td, &:last-child th': { border: 0 },
            cursor: onRowClick ? 'pointer' : 'default',
        }),
        [onRowClick]
    );

    return (
        <TableContainer component={Paper}>
            <Table sx={{ tableLayout: 'fixed', width: '100%' }}>
                <TableHeader table={table} filters={filters} columnFilters={columnFilters} onFiltersChange={onFiltersChange} />
                <TableBody>
                    {table.getRowModel().rows.length === 0 ? (
                        <TableRow sx={{ '&:hover': { bgcolor: 'transparent' } }}>
                            <TableCell
                                colSpan={table.getAllColumns().length}
                                sx={{
                                    textAlign: 'center',
                                    py: 2,
                                    wordBreak: 'break-word',
                                    overflowWrap: 'break-word',
                                    whiteSpace: 'normal',
                                }}
                            >
                                <Typography variant="body1" color="text.secondary">
                                    {noDataMessage}
                                </Typography>
                            </TableCell>
                        </TableRow>
                    ) : (
                        table.getRowModel().rows.map((row) => (
                            <TableRow key={row.id} hover sx={getRowStyles(row.index)} onClick={() => onRowClick?.(row.original)}>
                                {row.getVisibleCells().map((cell) => (
                                    <TableCell
                                        key={cell.id}
                                        sx={{
                                            width: cell.column.getSize(),
                                            maxWidth: cell.column.columnDef.maxSize || '200px',
                                            wordBreak: 'break-word',
                                            overflowWrap: 'break-word',
                                            whiteSpace: 'normal',
                                            hyphens: 'auto',
                                        }}
                                    >
                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                    </TableCell>
                                ))}
                            </TableRow>
                        ))
                    )}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

export default TableBase;
